/**
 * EODLocationReportTab
 * End-of-Day location report for Assistant HR (Sahil Karale).
 * Shows every employee's movement timeline with geofence badges (At Office / Field Visit).
 * Bulk export by department (Excel or PDF) for CMD reporting.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Navigation, RefreshCw, ChevronDown, ChevronUp, Filter,
         FileSpreadsheet, FileText, Download } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayIST() {
  const d = new Date(Date.now() + (5.5 * 60 - new Date().getTimezoneOffset()) * 60_000);
  return d.toISOString().slice(0, 10);
}
function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
  });
}

const DEPT_OPTIONS = [
  { value: '',             label: 'All Departments' },
  { value: 'dm_channel',  label: 'DM Channel' },
  { value: 'adm_channel', label: 'ADM Channel' },
  { value: 'cp_channel',  label: 'CP Channel' },
  { value: 'hr',          label: 'HR' },
  { value: 'marketing',   label: 'Marketing' },
  { value: 'finance',     label: 'Finance' },
  { value: 'operations',  label: 'Operations' },
  { value: 'admin',       label: 'Admin' },
  { value: 'it',          label: 'IT' },
];

// ── Geofence badge ─────────────────────────────────────────────────────────
function GeofenceBadge({ atOffice, distKm }) {
  if (atOffice) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        At Office
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-orange-100 text-orange-700 border border-orange-200 rounded-full px-1.5 py-0.5">
      <Navigation className="w-2.5 h-2.5" />
      Field Visit{distKm != null ? ` · ${distKm} km` : ''}
    </span>
  );
}

// ── Download helper ────────────────────────────────────────────────────────
async function triggerDownload(url, token, filename) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href; a.download = filename; a.click();
  URL.revokeObjectURL(href);
}

export default function EODLocationReportTab({ user }) {
  const token = localStorage.getItem('crm_token');
  const [date, setDate] = useState(todayIST());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState('');
  const [exporting, setExporting]   = useState('');   // 'excel' | 'pdf' | 'dept-excel' | 'dept-pdf' | ''
  const [bulkDept, setBulkDept]     = useState('dm_channel');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/location/eod-report/stored?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(data);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [token, date]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleGenerateNow = async () => {
    setGenerating(true);
    setGenMsg('');
    try {
      const { data } = await axios.post(`${API}/location/eod-report/store?date=${date}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGenMsg(`Report saved — ${data.tracked} employee(s) tracked.`);
      await fetchReport();
    } catch {
      setGenMsg('Failed to generate report.');
    } finally {
      setGenerating(false);
    }
  };

  // Single export (all depts or current filter)
  const handleExport = async (fmt) => {
    setExporting(fmt);
    try {
      const deptQ = deptFilter ? `&dept=${deptFilter}` : '';
      const ext   = fmt === 'excel' ? 'xlsx' : 'pdf';
      const scope = deptFilter
        ? DEPT_OPTIONS.find(o => o.value === deptFilter)?.label || deptFilter
        : 'All';
      await triggerDownload(
        `${API}/location/eod-report/export?date=${date}&format=${fmt}${deptQ}`,
        token,
        `Location_EOD_${scope}_${date}.${ext}`,
      );
    } catch { alert('Export failed. Please try again.'); }
    finally { setExporting(''); }
  };

  // Bulk export for a specific department
  const handleBulkExport = async (fmt) => {
    const key = `dept-${fmt}`;
    setExporting(key);
    try {
      const ext   = fmt === 'excel' ? 'xlsx' : 'pdf';
      const label = DEPT_OPTIONS.find(o => o.value === bulkDept)?.label || bulkDept;
      await triggerDownload(
        `${API}/location/eod-report/export?date=${date}&format=${fmt}&dept=${bulkDept}`,
        token,
        `Location_EOD_${label.replace(/ /g,'_')}_${date}.${ext}`,
      );
    } catch { alert('Bulk export failed. Please try again.'); }
    finally { setExporting(''); }
  };

  const filteredReport = (report?.report || []).filter(emp => {
    const q = search.trim().toLowerCase();
    const matchSearch = !q || `${emp.name} ${emp.employeeId} ${emp.department}`.toLowerCase().includes(q);
    const matchDept   = !deptFilter || emp.department === deptFilter;
    return matchSearch && matchDept;
  });

  const tracked = filteredReport.filter(e => e.hasData).length;

  return (
    <div className="space-y-4" data-testid="eod-location-report-tab">

      {/* ── Top Controls ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Navigation className="w-4 h-4 text-purple-600 flex-shrink-0" />
          <h2 className="text-sm font-bold text-stone-800 whitespace-nowrap">Location EOD Report</h2>
          <span className="text-[11px] text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full whitespace-nowrap">Auto-gen 10:30 PM IST</span>
          {report && !report.found && (
            <span className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Live</span>
          )}
        </div>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <input
            type="date" value={date} onChange={e => setDate(e.target.value)}
            data-testid="eod-location-date"
            className="px-3 py-1.5 text-xs border border-stone-300 rounded-md outline-none focus:ring-2 focus:ring-purple-400 bg-white"
          />
          <button
            onClick={handleGenerateNow} disabled={generating}
            data-testid="eod-generate-now-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Saving…' : 'Save Snapshot'}
          </button>
          <button
            onClick={() => handleExport('excel')} disabled={!!exporting || !report}
            data-testid="export-excel-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <FileSpreadsheet className={`w-3 h-3 ${exporting === 'excel' ? 'animate-pulse' : ''}`} />
            {exporting === 'excel' ? 'Exporting…' : 'Excel'}
          </button>
          <button
            onClick={() => handleExport('pdf')} disabled={!!exporting || !report}
            data-testid="export-pdf-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <FileText className={`w-3 h-3 ${exporting === 'pdf' ? 'animate-pulse' : ''}`} />
            {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </button>
        </div>
      </div>

      {genMsg && (
        <p className="text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">{genMsg}</p>
      )}

      {/* ── Bulk Export by Department ── */}
      <div
        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl px-4 py-3"
        data-testid="bulk-export-section"
      >
        <div className="flex items-center gap-2">
          <Download className="w-4 h-4 text-indigo-600 flex-shrink-0" />
          <span className="text-xs font-bold text-indigo-800">Bulk Export by Department</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-auto">
          <select
            value={bulkDept}
            onChange={e => setBulkDept(e.target.value)}
            data-testid="bulk-dept-select"
            className="text-xs font-medium border border-indigo-300 rounded-md px-2 py-1.5 outline-none focus:ring-2 focus:ring-indigo-400 bg-white text-indigo-800"
          >
            {DEPT_OPTIONS.filter(o => o.value).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={() => handleBulkExport('excel')}
            disabled={!!exporting}
            data-testid="bulk-export-excel-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <FileSpreadsheet className={`w-3 h-3 ${exporting === 'dept-excel' ? 'animate-pulse' : ''}`} />
            {exporting === 'dept-excel' ? 'Exporting…' : 'Export Excel'}
          </button>
          <button
            onClick={() => handleBulkExport('pdf')}
            disabled={!!exporting}
            data-testid="bulk-export-pdf-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <FileText className={`w-3 h-3 ${exporting === 'dept-pdf' ? 'animate-pulse' : ''}`} />
            {exporting === 'dept-pdf' ? 'Exporting…' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* ── HQ Info Bar ── */}
      <div className="flex items-center gap-2 text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
        <MapPin className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
        <span>
          <strong>Head Office:</strong> Siddhivinayak Infra, Akurdi, Pimpri-Chinchwad, Pune – 411035
          &nbsp;·&nbsp;
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500" /> At Office ≤ 500 m
          </span>
          &nbsp;·&nbsp;
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500" /> Field Visit &gt; 500 m
          </span>
        </span>
      </div>

      {/* ── Filters ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <svg className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" strokeWidth="2"/>
            <path d="m21 21-4.35-4.35" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, ID…"
            data-testid="eod-location-search"
            className="w-full pl-7 pr-3 py-1.5 text-xs border border-stone-300 rounded-md outline-none focus:ring-2 focus:ring-purple-400 bg-white"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter className="w-3.5 h-3.5 text-stone-400" />
          <select
            value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
            data-testid="eod-location-dept-filter"
            className="text-xs border border-stone-300 rounded-md px-2 py-1.5 outline-none focus:ring-2 focus:ring-purple-400 bg-white"
          >
            {DEPT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── Stats ── */}
      {report && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[
            { l: 'Total Employees', v: filteredReport.length, c: 'text-stone-700' },
            { l: 'Tracked Today',   v: tracked,               c: 'text-green-600' },
            { l: 'No Data',         v: filteredReport.length - tracked, c: 'text-stone-400' },
          ].map(s => (
            <div key={s.l} className="bg-white border border-stone-200 rounded-xl p-3 text-center">
              <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
              <p className="text-[11px] text-stone-500 uppercase tracking-wide">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Employee List ── */}
      {loading ? (
        <div className="p-8 text-center text-stone-400 text-sm">Loading location report…</div>
      ) : !report || filteredReport.length === 0 ? (
        <div className="p-8 text-center text-stone-400 text-sm">No location data for the selected date / filter.</div>
      ) : (
        <div className="space-y-2">
          {filteredReport.map(emp => {
            const officeStops = (emp.placesVisited || []).filter(p => p.atOffice).length;
            const fieldStops  = (emp.placesVisited || []).filter(p => !p.atOffice).length;
            return (
              <div key={emp.email} className="bg-white border border-stone-200 rounded-xl overflow-hidden"
                data-testid={`eod-emp-row-${emp.employeeId}`}>

                {/* Header row */}
                <button
                  onClick={() => setExpanded(prev => ({ ...prev, [emp.email]: !prev[emp.email] }))}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors text-left"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ background: emp.hasData ? '#7c3aed' : '#94a3b8' }}
                  >
                    {emp.name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-stone-800">{emp.name}</p>
                      <span className="text-[10px] text-stone-400">{emp.employeeId} · {emp.department?.toUpperCase()}</span>
                      {emp.hasData && officeStops > 0 && (
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">
                          {officeStops} office
                        </span>
                      )}
                      {emp.hasData && fieldStops > 0 && (
                        <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-semibold">
                          {fieldStops} field
                        </span>
                      )}
                      {!emp.hasData && (
                        <span className="text-[10px] bg-stone-100 text-stone-400 px-1.5 py-0.5 rounded-full">No data</span>
                      )}
                    </div>
                    {emp.hasData && emp.firstAddress && (
                      <div className="mt-0.5 text-[11px] text-stone-500 truncate">
                        <span className="text-green-600 font-medium">First seen:</span>{' '}
                        {emp.firstAddress.substring(0, 55)}…
                      </div>
                    )}
                  </div>
                  {emp.hasData && (
                    expanded[emp.email]
                      ? <ChevronUp className="w-4 h-4 text-stone-400 flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-stone-400 flex-shrink-0" />
                  )}
                </button>

                {/* Timeline expand */}
                {expanded[emp.email] && emp.hasData && (
                  <div className="border-t border-stone-100 px-4 py-3 bg-stone-50">
                    <p className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-3">
                      Movement Timeline — {date}
                    </p>
                    {(emp.placesVisited || []).length > 0 ? (
                      <div className="relative">
                        <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-stone-200" />
                        <div className="space-y-1">
                          {emp.placesVisited.map((p, i) => (
                            <div key={i} className="flex items-start gap-3 py-1.5 relative">
                              <div className="w-10 text-center flex-shrink-0 z-10">
                                <div className={`w-3 h-3 rounded-full border-2 border-white shadow mx-auto mt-0.5 ${p.atOffice ? 'bg-emerald-500' : 'bg-orange-500'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[10px] text-stone-400 font-mono">{fmtTime(p.time)}</span>
                                  <GeofenceBadge atOffice={p.atOffice} distKm={p.distanceFromHQkm} />
                                </div>
                                <div className="flex items-start gap-1 mt-0.5">
                                  <MapPin className={`w-3 h-3 mt-0.5 flex-shrink-0 ${p.atOffice ? 'text-emerald-500' : 'text-blue-500'}`} />
                                  <p className="text-[12px] text-stone-700 leading-snug">{p.address}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-[11px] text-stone-400">GPS recorded but no address available.</p>
                    )}
                    {emp.timeline?.length > 0 && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${emp.timeline.at(-1)?.lat},${emp.timeline.at(-1)?.lng}`}
                        target="_blank" rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 py-1 px-3 rounded-lg bg-blue-50 text-blue-600 text-[11px] font-medium hover:bg-blue-100 transition-colors"
                      >
                        <MapPin className="w-3 h-3" /> View last location on Google Maps
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
